import { api } from "@/lib/api";

/** Three states, three quiet tones – colour is reserved for what is actually tight or over. */
export const TILSTAND = {
  ledig: { label: "Ledig", cell: "text-foreground/70", chip: "bg-black/[0.05] text-foreground/70" },
  stramt: { label: "Stramt", cell: "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))] font-medium", chip: "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))]" },
  over: { label: "Over kapasitet", cell: "bg-[hsl(var(--v-blocked-bg))] text-[hsl(var(--v-blocked))] font-semibold", chip: "bg-[hsl(var(--v-blocked-bg))] text-[hsl(var(--v-blocked))]" },
  ukjent: { label: "Ukjent", cell: "text-muted-foreground", chip: "bg-black/[0.05] text-muted-foreground" },
};
export const STATE = {
  ok: { label: "Dekket", tone: "ok" },
  stramt: { label: "Stramt", tone: "warn" },
  tiltak: { label: "Krever tiltak", tone: "bad" },
  bekreftet: { label: "Bekreftet arbeid", tone: "ok" },
  mangler_periode: { label: "Verkstedopphold ikke satt", tone: "muted" },
  ikke_relevant: { label: "Ingen timer", tone: "muted" },
};
export const TONE_CHIP = {
  ok: "v-ok",
  warn: "bg-[hsl(var(--v-assumed-bg))] text-[hsl(var(--v-assumed))]",
  bad: "bg-[hsl(var(--v-blocked-bg))] text-[hsl(var(--v-blocked))]",
  muted: "bg-black/[0.05] text-muted-foreground",
};
export const BELEGG_TYPE = { bekreftet: "Bekreftet", vunnet: "Vunnet arbeid", reservasjon: "Reservasjon", tilbud: "Åpent tilbud", utlopt: "Utløpt reservasjon", vedlikehold: "Vedlikehold" };
export const KILDE = { manuell: "registrert", import: "importert", demo: "demo", sak: "fra sak" };

/** The five surfaces of /plan – the calendar first. Old links (`avdelinger`, `ressurser`) still land somewhere sensible. */
export const VIEWS = [
  { key: "oversikt", label: "Oversikt" },
  { key: "anlegg", label: "Dokk, slipp og kai" },
  { key: "maskiner", label: "Maskiner" },
  { key: "fag", label: "Fag · uker" },
  { key: "belegg", label: "Belegg" },
];
export const viewOf = (v) => ({ avdelinger: "fag", ressurser: "anlegg" }[v] || (VIEWS.some((x) => x.key === v) ? v : "oversikt"));
export const HORIZONS = [4, 8, 12, 16, 26];

/** Resource groups in the calendar – derived from type and name, so the settings need no extra field. */
export const GROUPS = [
  { key: "dokk", label: "Dokk" },
  { key: "slipp", label: "Slipp" },
  { key: "kai", label: "Kaier" },
  { key: "delt", label: "Kraner og delt utstyr" },
  { key: "maskiner", label: "Maskiner" },
];
export const groupOf = (r) => {
  if (r.type === "timer") return "maskiner";
  if (r.type === "delt") return "delt";
  const s = `${r.name || ""} ${r.id || ""}`;
  return /dokk/i.test(s) ? "dokk" : /slipp/i.test(s) ? "slipp" : /kai/i.test(s) ? "kai" : "delt";
};

/** Build a /plan URL from the current params and a patch (null/""/false removes the key). */
export const withPlanParams = (params, patch) => {
  const n = new URLSearchParams(params);
  Object.entries(patch).forEach(([k, v]) => (v == null || v === "" || v === false ? n.delete(k) : n.set(k, String(v))));
  const s = n.toString();
  return `/plan${s ? `?${s}` : ""}`;
};
export const listParam = (params, key) => (params.get(key) || "").split(",").filter(Boolean);
export const toggleIn = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

export const MONTHS = ["januar", "februar", "mars", "april", "mai", "juni", "juli", "august", "september", "oktober", "november", "desember"];
export const MONTHS_SHORT = ["jan", "feb", "mar", "apr", "mai", "jun", "jul", "aug", "sep", "okt", "nov", "des"];

export const pct = (v) => (v == null ? "–" : `${Math.round(v)} %`);
export const hrs = (v) => (v == null ? "–" : `${Number(v).toLocaleString("nb-NO", { maximumFractionDigits: 1 })} t`);
export const hrs0 = (v) => (v == null ? "–" : `${Math.round(Number(v)).toLocaleString("nb-NO")} t`);
export const nok = (v) => `${Math.round(v).toLocaleString("nb-NO")} kr`;
export const dmy = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "–");
export const dm = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : "–");
export const DAY = 86400000;
export const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
export const daysBetween = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / DAY);
export const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
/** «om 3 d» / «i dag» / «for 2 d siden» */
export const relDays = (iso) => { const n = daysBetween(todayISO(), iso); return n === 0 ? "i dag" : n > 0 ? `om ${n} d` : `for ${-n} d siden`; };
export const isoWeekOf = (d = new Date()) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const y = t.getUTCFullYear();
  const w = Math.ceil(((t - Date.UTC(y, 0, 1)) / 86400000 + 1) / 7);
  return `${y}-W${String(w).padStart(2, "0")}`;
};
export const weekStart = (key) => {
  const [y, w] = key.split("-W").map(Number);
  const jan4 = new Date(Date.UTC(y, 0, 4));
  const mon = new Date(jan4);
  mon.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() || 7) - 1) + (w - 1) * 7);
  return mon;
};
export const shiftWeek = (key, n) => {
  const d = weekStart(key);
  d.setUTCDate(d.getUTCDate() + n * 7);
  return isoWeekOf(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};
export const weekNo = (key) => Number(key.split("-W")[1]);
export const toISO = (d) => d.toISOString().slice(0, 10);
/** ISO week number of a date, and the week key («2026-W42») it belongs to. */
export const weekNoOf = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  return Math.ceil(((d - Date.UTC(d.getUTCFullYear(), 0, 1)) / DAY + 1) / 7);
};
export const isoWeekKeyOf = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  return `${d.getUTCFullYear()}-W${String(weekNoOf(iso)).padStart(2, "0")}`;
};
export const weekStartISO = (key) => toISO(weekStart(key));

/** Semicolon- or comma-separated text → rows keyed by the header. Lines starting with # are notes. */
export function parseCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l && !l.startsWith("#"));
  if (!lines.length) return [];
  const sep = lines[0].includes(";") ? ";" : lines[0].includes("\t") ? "\t" : ",";
  const head = lines[0].split(sep).map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((l) => Object.fromEntries(l.split(sep).map((v, i) => [head[i], v.trim()])));
}

export const kapasitetApi = {
  oversikt: (params) => api.get("/kapasitet/oversikt", { params }),
  puls: (params) => api.get("/kapasitet/puls", { params }),
  celle: (avdeling, uke) => api.get("/kapasitet/celle", { params: { avdeling, uke } }),
  ressurser: (params) => api.get("/kapasitet/ressurser", { params }),
  belegg: () => api.get("/kapasitet/belegg"),
  addBelegg: (body) => api.post("/kapasitet/belegg", body),
  patchBelegg: (id, body) => api.patch(`/kapasitet/belegg/${id}`, body),
  delBelegg: (id) => api.delete(`/kapasitet/belegg/${id}`),
  delDemo: () => api.delete("/kapasitet/belegg/demo"),
  seedDemo: () => api.post("/kapasitet/belegg/demo"),
  importer: (rader) => api.post("/kapasitet/belegg/import", { rader }),
  mal: () => api.get("/kapasitet/belegg/mal", { responseType: "text" }),
  innstillinger: () => api.get("/kapasitet/innstillinger"),
  lagre: (body) => api.put("/kapasitet/innstillinger", body),
  sak: (cid) => api.get(`/projects/${cid}/kapasitet`),
  plan: (cid, body) => api.patch(`/projects/${cid}/kapasitet/plan`, body),
  forslag: (cid, handling) => api.post(`/projects/${cid}/kapasitet/forslag`, { handling }),
  reserver: (cid, body) => api.post(`/projects/${cid}/kapasitet/reserver`, body),
  frigi: (cid) => api.delete(`/projects/${cid}/kapasitet/reserver`),
};
